/**
 * What to tell somebody who opened the system in a phone's browser. Request of
 * 6 October 2026: everybody on a phone is offered the app, and pointed at
 * Google Chrome, which is the browser that installs it properly on Android.
 *
 * - `prompt`: Chrome on Android has offered its own install, one tap away.
 * - `chromeMenu`: Chrome on Android, with no offer yet: the menu has it.
 * - `openChrome`: another Android browser: open the page in Chrome first.
 * - `ios`: an iPhone or iPad installs from the Share button.
 * - `none`: a desk, or the app is already installed.
 */
export type InstallAdvice = 'none' | 'prompt' | 'chromeMenu' | 'openChrome' | 'ios';

export interface InstallContext {
  userAgent: string;
  /** Running as the installed app already. */
  standalone: boolean;
  /** Chrome has handed over its install offer. */
  canPrompt: boolean;
}

const OTHER_ANDROID_BROWSER =
  /SamsungBrowser|EdgA|OPR\/|Firefox|; wv\)|FBAN|FBAV|Instagram|MiuiBrowser|UCBrowser/;

export function installAdvice({ userAgent, standalone, canPrompt }: InstallContext): InstallAdvice {
  if (standalone) return 'none';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (!userAgent.includes('Android')) return 'none';
  if (canPrompt) return 'prompt';
  const chrome = userAgent.includes('Chrome/') && !OTHER_ANDROID_BROWSER.test(userAgent);
  return chrome ? 'chromeMenu' : 'openChrome';
}

/** Whether an iPhone or iPad is running the installed app, whose icon never refreshes itself. */
export function keepsItsIcon({ userAgent, standalone }: Omit<InstallContext, 'canPrompt'>): boolean {
  return standalone && /iPhone|iPad|iPod/.test(userAgent);
}
